import React, { createContext, useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { setUser, clearUser, setLoading, setError } from './store/userSlice';
import axios from 'axios';
import API_BASE_URL from "./apiConfig";

export const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const dispatch = useDispatch();

  // 1) on mount, fetch current user
  useEffect(() => {
    dispatch(setLoading(true));
    axios
      .get(`${API_BASE_URL}/api/auth/me`, { withCredentials: true })
      .then(res => {
        dispatch(setUser(res.data));
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(clearUser());
        dispatch(setLoading(false));
      });
  }, [dispatch]);

  // 2) login() calls backend, sets cookie + user
  const login = async (credentials) => {
    dispatch(setLoading(true));
    try {
      let res;
      
      if (credentials.googleUser) {
        // Handle Google OAuth login - user data is already fetched from backend
        dispatch(setUser(credentials.googleUser));
      } else {
        // Handle email/password login
        res = await axios.post(
          `${API_BASE_URL}/api/auth/login`,
          { email: credentials.email, password: credentials.password },
          { withCredentials: true }
        );
        dispatch(setUser(res.data));
      }
      
      dispatch(setLoading(false));
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      dispatch(setError(error.response?.data?.error || 'Login failed'));
      dispatch(setLoading(false));
      throw error;
    }
  };

  // 3) logout() clears cookie + user
  const logout = async () => {
    dispatch(setLoading(true));
    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      dispatch(clearUser());
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setError(error.response?.data?.error || 'Logout failed'));
      // Even if logout fails, clear the user from frontend
      dispatch(clearUser());
      dispatch(setLoading(false));
    }
  };

  return (
    <AuthContext.Provider value={{ login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
